import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Menu,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Column } from '../../types/common';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CustomTooltip from '../CustomToolTip/CustomToolTip';

export type Rating = 'High' | 'Medium' | 'Low';
export type Impact = 'Breakthrough' | 'Transformative' | 'Incremental';

export type EnhancedDataTableProps<T> = {
  columns: Column<T>[];
  rows: T[];
  title?: string;
  onEdit?: (row: T, rowIndex: number) => void;
  onDuplicate?: (row: T) => void;
  onDelete?: (row: T) => void;
  onFavorite?: (row: T) => void;
  onView?: (row: T) => void;
  onClick?: (row: T) => void;
  sortConfig?: {
    key: keyof T | null;
    direction: 'asc' | 'desc';
  };
  onSort?: (key: keyof T) => void;
  onchange?: (
    columnName: string,
    changedValue: string,
    row: T,
    rowIndex: number,
    columnId: string | number | symbol
  ) => void;
  onAttachmentClick?: (row: T) => void;
  onSaveEdit?: (
    row: T,
    rowIndex: number,
    columnId: string | number | symbol,
    newValue: string
  ) => Promise<void>;
  onRename?: (rowId: string, newValue: string) => Promise<void>;
  renameColumnId?: string | number | symbol;
  height?: string;
  loading?: boolean;
  loadingMessage?: string;
  initialSort?: {
    key: keyof T;
    direction: 'asc' | 'desc';
  };
  showRowBorders?: boolean;
  headerFontWeight?: number;
  showActionsMenu?: boolean;
  showRowCursor?: boolean;
  customColorMap?: Record<string, { bgColor: string; color: string }>;
  hideScrollBar?: boolean;
};

interface LoaderProps {
  message?: string;
  height?: string;
}

const Loader: React.FC<LoaderProps> = ({ message = 'Fetching Data Sources', height = '300px' }) => {
  const segments = Array.from({ length: 20 }, (_, index) => {
    const rotation = index * 18;
    const opacity = 0.2 + (index / 20) * 0.8;

    return (
      <Box
        key={index}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '3px',
          height: '6px',
          backgroundColor: '#2563EB',
          borderRadius: '2px',
          transformOrigin: '50% 34px',
          transform: `translate(-50%, -100%) rotate(${rotation}deg)`,
          opacity: opacity,
          animation: `fadeRotate 1.2s linear infinite`,
          animationDelay: `${index * 0.06}s`,
          '@keyframes fadeRotate': {
            '0%': { opacity: 0.2 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.2 },
          },
        }}
      />
    );
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: height,
        width: '100%',
        backgroundColor: '#FFFFFF',
        padding: 2,
      }}
    >
      <Box
        sx={{
          width: '68px',
          height: '68px',
          position: 'relative',
        }}
      >
        {segments}
      </Box>

      <Typography
        sx={{
          mt: 5,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '24px',
          color: '#AEAEAE',
          textAlign: 'center',
          maxWidth: '400px',
        }}
      >
        {message}
      </Typography>
    </Box>
  );
};

const colorMap: Record<string, { bgColor: string; color: string }> = {
  High: { bgColor: '#BEF2B2', color: '#429230' },
  Medium: { bgColor: '#F2ECB2', color: '#928D30' },
  Low: { bgColor: '#ffcdd2', color: '#c62828' },
  Ready: { bgColor: '#BEF2B2', color: '#429230' },
  Review: { bgColor: '#F2ECB2', color: '#FED73B' },
  Pending: { bgColor: '#ffcdd2', color: '#c62828' },
};

const statusColorMap: Record<string, { bgColor: string; color: string; border?: string }> = {
  'Draft: Solution Requirements': {
    bgColor: '#E7E7E7',
    color: '#6E6E6E',
  },
  'Draft: Data Sources': {
    bgColor: '#DDE2EB',
    color: '#5C5D68',
  },
  'Report In Progress': {
    bgColor: '#E6FFE5',
    color: '#15B535',
  },
  'Report Generated': {
    bgColor: '#C6ECC9',
    color: '#006F18',
  },
  'Summary In Progress': {
    bgColor: '#FFF8E5',
    color: '#FF972F',
  },
  'Summary Generated': {
    bgColor: '#FFEFEB',
    color: '#FF6E49',
  },
  'Summary - Approved': {
    bgColor: '#C6ECC9',
    color: '#006F18',
  },
  'Summary - Pending': {
    bgColor: '#FFF8E5',
    color: '#CE6700',
  },
  'Summary - Unapproved': {
    bgColor: '#ff4b4b1c',
    color: '#c44545',
  },
  'Report - Unapproved': {
    bgColor: '#ff4b4b1c',
    color: 'red',
  },
  'Report - Approved': {
    bgColor: '#C6ECC9',
    color: '#006F18',
  },
  'Report - Pending': {
    bgColor: '#FFF8E5',
    color: '#CE6700',
  },
  'Draft - Unapproved': {
    bgColor: '#ff4b4b1c',
    color: 'red',
  },
  'Draft - Approved': {
    bgColor: '#C6ECC9',
    color: '#006F18',
  },
  'Draft - Pending': {
    bgColor: '#FFF8E5',
    color: '#CE6700',
  },
  'Sources - Approved': {
    bgColor: '#C6ECC9',
    color: '#006F18',
  },
  'Sources - Pending': {
    bgColor: '#FFF8E5',
    color: '#CE6700',
  },
  'Sources - Unapproved': {
    bgColor: '#ff4b4b1c',
    color: '#c44545',
  },
  'Source - Pending': {
    bgColor: '#FFF8E5',
    color: '#CE6700',
  },
  'Source - Approved': {
    bgColor: '#C6ECC9',
    color: '#006F18',
  },
  'Source - Unapproved': {
    bgColor: '#ff4b4b1c',
    color: '#c44545',
  },
  High: {
    bgColor: '#C6ECC9',
    color: '#006F18',
  },
  Medium: {
    bgColor: '#F2ECB2',
    color: '#928D30',
  },
  Low: {
    bgColor: '#ffcdd2',
    color: '#c62828',
  },
};

const TagView = ({
  value,
  onClick,
  isStatus = false,
  customColorMap,
}: {
  value: string;
  onClick?: () => void;
  isStatus?: boolean;
  customColorMap?: Record<string, { bgColor: string; color: string }>;
}) => {
  const statusStyles = isStatus && statusColorMap[value] ? statusColorMap[value] : null;
  const customStyles = customColorMap && customColorMap[value] ? customColorMap[value] : null;

  const styles: { bgColor: string; color: string; border?: string } = statusStyles ||
    customStyles || {
      bgColor: '#fff',
      color: '#737373',
      border: '1px solid #D9D9D9',
    };

  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '6px',
        backgroundColor: styles.bgColor,
        color: styles.color,
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'Poppins, sans-serif',
        lineHeight: '20px',
        cursor: onClick ? 'pointer' : 'default',
        border: styles.border || 'none',
        minHeight: '28px',
        whiteSpace: 'nowrap',
        '&:hover': onClick
          ? {
              opacity: 0.8,
            }
          : {},
      }}
    >
      {value}
    </Box>
  );
};

const DropDownCell = ({
  value,
  editable,
  options,
  onChange,
  align,
  placeholder = '',
}: {
  value: string;
  editable?: boolean;
  options: string[];
  onChange?: (newRating: string) => void;
  align?: 'right' | 'left' | 'center';
  placeholder?: string;
}) => {
  let color = '#000';
  let underlineColor = 'transparent';
  let showUnderline = false;

  const [isClicked, setIsClicked] = React.useState(false);

  if (['High', 'Medium', 'Low', 'Ready', 'Review', 'Pending'].includes(value)) {
    const colors = colorMap[value as Rating];
    color = '#000000';
    underlineColor = colors.color;
    showUnderline = true;
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange?.(event.target.value);
    setIsClicked(false);
  };

  const displayValue =
    ['High', 'Medium', 'Low'].includes(value) && value ? value.toUpperCase() : value || placeholder;

  return (
    <Box
      sx={{
        color,
        border: `${editable ? `1px solid ${isClicked ? '#96c5f4' : '#D4DADF'}` : ''}`,
        borderRadius: '6px',
        paddingX: '8px',
        paddingY: '6px',
        marginRight: '5px',
        minHeight: '36px',
        width: '98%',
        cursor: editable ? 'pointer' : 'default',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
        ...(showUnderline && {
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '9px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '70%',
            height: '3px',
            backgroundColor: underlineColor,
            borderRadius: '2px',
          },
        }),
      }}
    >
      {editable ? (
        <FormControl fullWidth>
          <Select
            value={value || ''}
            onChange={handleChange}
            onMouseDown={() => setIsClicked(true)}
            onBlur={() => setIsClicked(false)}
            displayEmpty
            renderValue={(selected) =>
              selected ? (
                ['High', 'Medium', 'Low'].includes(selected) ? (
                  selected.toUpperCase()
                ) : (
                  selected
                )
              ) : (
                <Typography style={{ color: '#999' }}>{placeholder}</Typography>
              )
            }
            MenuProps={{
              disableScrollLock: true,
              PaperProps: {
                style: {
                  maxHeight: 'none',
                },
              },
            }}
            sx={{
              color,
              height: '24px',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: 0,
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
              padding: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {options.map((option) => (
              <MenuItem key={option} value={option}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: 0,
                    verticalAlign: 'middle',
                  }}
                >
                  {['High', 'Medium', 'Low'].includes(option) ? option.toUpperCase() : option}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: 0,
            verticalAlign: 'middle',
            padding: 0,
            margin: 0,
            color: value ? '#000' : '#999',
          }}
        >
          {displayValue}
        </Typography>
      )}
    </Box>
  );
};

const EditableTextCell = ({
  value,
  editable,
  onChange,
  onSaveEdit,
  onRename,
  placeholder = '',
  isInEditMode = false,
  onEditModeChange,
  row,
  rowIndex,
  columnId,
}: {
  value: string;
  editable?: boolean;
  onChange?: (newValue: string) => void;
  onSaveEdit?: (
    row: any,
    rowIndex: number,
    columnId: string | number | symbol,
    newValue: string
  ) => Promise<void>;
  onRename?: (rowId: string, newValue: string) => Promise<void>;
  placeholder?: string;
  isInEditMode?: boolean;
  onEditModeChange?: (editing: boolean) => void;
  row?: any;
  rowIndex?: number;
  columnId?: string | number | symbol;
}) => {
  const [text, setText] = React.useState(value);
  const [isSaving, setIsSaving] = React.useState(false);
  const [hoveredIcon, setHoveredIcon] = React.useState<'save' | 'cancel' | null>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  const breakText = (input: string, wordsPerLine = 5) => {
    return input
      .split(' ')
      .reduce((acc, word, idx) => {
        const lineIndex = Math.floor(idx / wordsPerLine);
        acc[lineIndex] = (acc[lineIndex] || '') + word + ' ';
        return acc;
      }, [] as string[])
      .join('\n')
      .trim();
  };

  const handleSave = async () => {
    if (text !== value) {
      setIsSaving(true);
      try {
        if (onRename && row && row?.id) {
          await onRename(row?.id, text);
        } else if (onSaveEdit && row && rowIndex !== undefined && columnId) {
          await onSaveEdit(row, rowIndex, columnId, text);
        } else if (onChange) {
          onChange(text);
        }
      } catch (error) {
        console.error('Error saving edit:', error);
      } finally {
        setIsSaving(false);
      }
    }
    onEditModeChange?.(false);
  };

  const handleDiscard = () => {
    setText(value);
    onEditModeChange?.(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    }
    if (e.key === 'Escape') {
      handleDiscard();
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    if (isInEditMode) {
      e.stopPropagation();
    } else if (editable && !isInEditMode) {
      onEditModeChange?.(true);
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Box
      onClick={handleContainerClick}
      sx={{
        width: '100%',
        cursor: editable && !isInEditMode ? 'pointer' : 'default',
        position: 'relative',
        border: `1px solid ${isInEditMode ? '#96c5f4' : '#D4DADF'}`,
        borderRadius: '6px',
        padding: isInEditMode ? '6px 60px 6px 8px' : '6px 8px',
        minHeight: '36px',
        maxHeight: '36px',
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
      }}
    >
      {isInEditMode ? (
        <>
          <TextField
            value={text}
            autoFocus
            variant="standard"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={handleInputClick}
            disabled={isSaving}
            sx={{
              flex: 1,
              minWidth: 0,
            }}
            InputProps={{
              disableUnderline: true,
              sx: {
                typography: 'body2',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: 0,
                padding: 0,
                margin: 0,
                height: '20px',
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '4px',
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              disabled={isSaving}
              onMouseEnter={() => setHoveredIcon('save')}
              onMouseLeave={() => setHoveredIcon(null)}
              sx={{
                width: '20px',
                height: '20px',
                padding: '2px',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <img
                src={hoveredIcon === 'save' ? 'images/SuccessHover.svg' : 'images/Success.svg'}
                alt="check-icon"
                width="16"
                height="16"
              />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDiscard();
              }}
              disabled={isSaving}
              onMouseEnter={() => setHoveredIcon('cancel')}
              onMouseLeave={() => setHoveredIcon(null)}
              sx={{
                width: '20px',
                height: '20px',
                padding: '2px',
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <img
                src={hoveredIcon === 'cancel' ? 'images/CrossHover.svg' : 'images/Cross.svg'}
                alt="cross-icon"
                width="16"
                height="16"
              />
            </IconButton>
          </Box>
        </>
      ) : (
        <CustomTooltip
          title={text.length > 30 ? breakText(text, 5) : ''}
          variant="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                whiteSpace: 'pre-line',
                bgcolor: 'white',
                color: '#666',
                fontSize: '0.875rem',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                px: 2,
                py: 1.5,
                maxWidth: 800,
                ml: 2,
                lineHeight: 1.4,
                '& .MuiTooltip-arrow': {
                  color: 'white',
                  '&::before': {
                    border: '1px solid #e0e0e0',
                  },
                },
              },
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: 0,
              color: text.length > 0 ? 'black' : '#8D959A',
              verticalAlign: 'middle',
              padding: 0,
              margin: 0,
            }}
          >
            {text?.length ? (text.length > 30 ? `${text.slice(0, 30)}...` : text) : placeholder}
          </Typography>
        </CustomTooltip>
      )}
    </Box>
  );
};

interface ActionMenuProps<T> {
  row: T;
  rowIndex: number;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onDuplicate?: (row: T) => void;
  onDelete?: (row: T) => void;
  onRename?: (row: T, rowIndex: number) => void;
  showEdit?: boolean;
  showDuplicate?: boolean;
  showDelete?: boolean;
}

function ActionMenu<T>({
  row,
  rowIndex,
  anchorEl,
  open,
  onClose,
  onDuplicate,
  onDelete,
  onRename,
  showDuplicate = true,
  showDelete = true,
}: ActionMenuProps<T>) {
  const handleRename = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRename?.(row, rowIndex);
    onClose();
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDuplicate?.(row);
    onClose();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(row);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      disablePortal={false}
      slotProps={{
        root: { sx: { zIndex: 4000 } },
        paper: { sx: { zIndex: 4000 } },
      }}
      PaperProps={{
        sx: {
          minWidth: 120,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          zIndex: 4000,
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <MenuItem
        onClick={handleRename}
        sx={{
          fontSize: '14px',
          fontFamily: 'Inter, sans-serif',
          py: 1,
          px: 2,
          '&:hover': {
            backgroundColor: '#F3F4F6',
          },
        }}
      >
        <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 400 }}>
          Rename
        </Typography>
      </MenuItem>
      {showDuplicate && (
        <MenuItem
          onClick={handleDuplicate}
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
            py: 1,
            px: 2,
            '&:hover': {
              backgroundColor: '#F3F4F6',
            },
          }}
        >
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 400 }}>
            Duplicate
          </Typography>
        </MenuItem>
      )}
      {showDelete && (
        <MenuItem
          onClick={handleDelete}
          sx={{
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
            py: 1,
            px: 2,
            color: '#FD4D4D',
            '&:hover': {
              backgroundColor: '#F3F4F6',
            },
          }}
        >
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 400 }}>
            Delete
          </Typography>
        </MenuItem>
      )}
    </Menu>
  );
}

const extractTextFromReactElement = (element: any): string => {
  if (!element) return '';

  if (typeof element === 'string') {
    return element;
  }

  if (typeof element === 'number') {
    return element.toString();
  }

  if (React.isValidElement(element)) {
    const props = element.props as any;
    if (props && props.children) {
      const children = props.children;

      if (Array.isArray(children)) {
        return children.map((child: any) => extractTextFromReactElement(child)).join('');
      }

      return extractTextFromReactElement(children);
    }
  }

  if (element && typeof element === 'object' && element.text) {
    return element.text;
  }

  return '';
};

const parseDate = (dateString: string): Date => {
  if (!dateString || typeof dateString !== 'string') {
    return new Date('');
  }

  const directParse = new Date(dateString);
  if (!isNaN(directParse.getTime())) {
    return directParse;
  }

  const timestamp = parseFloat(dateString);
  if (!isNaN(timestamp)) {
    const date = timestamp > 1e10 ? new Date(timestamp) : new Date(timestamp * 1000);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  const formats = [
    /(\d{4})-(\d{1,2})-(\d{1,2})[T\s](\d{1,2}):(\d{1,2}):(\d{1,2})/,
    /(\d{4})-(\d{1,2})-(\d{1,2})/,
    /(\d{1,2})\/(\d{1,2})\/(\d{4})[,\s]*(\d{1,2}):(\d{1,2}):(\d{1,2})/,
    /(\d{1,2})\/(\d{1,2})\/(\d{4})/,
    /(\d{1,2})-(\d{1,2})-(\d{4})/,
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,]+(\d{1,2})[\s,]+(\d{4})/i,
  ];

  for (const format of formats) {
    const match = dateString.match(format);
    if (match) {
      if (format.source.includes('Jan|Feb')) {
        const monthNames = [
          'jan',
          'feb',
          'mar',
          'apr',
          'may',
          'jun',
          'jul',
          'aug',
          'sep',
          'oct',
          'nov',
          'dec',
        ];
        const monthIndex = monthNames.findIndex((m) => match[1].toLowerCase().startsWith(m));
        if (monthIndex !== -1) {
          const parsedDate = new Date(parseInt(match[3]), monthIndex, parseInt(match[2]));
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
          }
        }
      } else if (match.length >= 4) {
        const [, first, second, third, hour, minute, second_time] = match;

        const attempts = [
          hour
            ? new Date(
                parseInt(first),
                parseInt(second) - 1,
                parseInt(third),
                parseInt(hour),
                parseInt(minute),
                parseInt(second_time || '0')
              )
            : new Date(parseInt(first), parseInt(second) - 1, parseInt(third)),
          hour
            ? new Date(
                parseInt(third),
                parseInt(first) - 1,
                parseInt(second),
                parseInt(hour),
                parseInt(minute),
                parseInt(second_time || '0')
              )
            : new Date(parseInt(third), parseInt(first) - 1, parseInt(second)),
          hour
            ? new Date(
                parseInt(third),
                parseInt(second) - 1,
                parseInt(first),
                parseInt(hour),
                parseInt(minute),
                parseInt(second_time || '0')
              )
            : new Date(parseInt(third), parseInt(second) - 1, parseInt(first)),
        ];

        for (const attempt of attempts) {
          if (
            !isNaN(attempt.getTime()) &&
            attempt.getFullYear() > 1900 &&
            attempt.getFullYear() < 2100
          ) {
            return attempt;
          }
        }
      }
    }
  }

  return new Date('');
};

export function EnhancedDataTable<T extends object>({
  columns,
  rows,
  title,
  onFavorite,
  onView,
  onDelete,
  onEdit,
  onDuplicate,
  onSort,
  onchange,
  onSaveEdit,
  onRename,
  renameColumnId,
  onClick,
  onAttachmentClick,
  sortConfig,
  height = 'auto',
  loading = false,
  loadingMessage = 'Fetching Data Sources',
  initialSort,
  showRowBorders = false,
  headerFontWeight = 600,
  showActionsMenu = false,
  showRowCursor = true,
  customColorMap,
  hideScrollBar = false,
}: EnhancedDataTableProps<T>) {
  const [rowsData, setRowsData] = useState(rows);
  const [internalSort, setInternalSort] = useState<{
    key: keyof T | null;
    direction: 'asc' | 'desc';
  }>({
    key: initialSort?.key || null,
    direction: initialSort?.direction || 'desc',
  });

  const [actionMenuState, setActionMenuState] = useState<{
    anchorEl: HTMLElement | null;
    rowIndex: number | null;
  }>({
    anchorEl: null,
    rowIndex: null,
  });

  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    columnId: string | number | symbol;
  } | null>(null);

  useEffect(() => {
    setRowsData(rows);
  }, [rows]);

  const handleSort = (column: Column<T>) => {
    if (column.sortable && column.id !== 'actions') {
      let direction: 'asc' | 'desc' = 'asc';
      if (internalSort.key === column.id) {
        direction = internalSort.direction === 'asc' ? 'desc' : 'asc';
      }
      setInternalSort({ key: column.id as keyof T, direction });
      onSort?.(column.id as keyof T);
    }
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, rowIndex: number) => {
    event.preventDefault();
    event.stopPropagation();
    setActionMenuState({
      anchorEl: event.currentTarget,
      rowIndex,
    });
  };

  const handleActionMenuClose = () => {
    setActionMenuState({
      anchorEl: null,
      rowIndex: null,
    });
  };

  const handleRename = (_row: T, rowIndex: number) => {
    if (renameColumnId) {
      setEditingCell({ rowIndex, columnId: renameColumnId });
    }
  };

  useEffect(() => {
    const sortKey = sortConfig?.key || internalSort.key;
    const direction = sortConfig?.direction || internalSort.direction;

    if (sortKey) {
      const sorted = [...rows].sort((a, b) => {
        const aValue = a[sortKey];
        const bValue = b[sortKey];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        const aText = extractTextFromReactElement(aValue);
        const bText = extractTextFromReactElement(bValue);

        const column = columns.find((col) => col.id === sortKey);
        const sortType = column?.sortType;

        if (sortType === 'time' || sortType === 'datetime') {
          let aDateValue: unknown = aValue;
          let bDateValue: unknown = bValue;

          if (sortKey === 'lastUpdated') {
            const aWithRaw = a as any;
            const bWithRaw = b as any;
            if (aWithRaw?.lastUpdatedRaw != null && bWithRaw?.lastUpdatedRaw != null) {
              aDateValue = aWithRaw.lastUpdatedRaw;
              bDateValue = bWithRaw.lastUpdatedRaw;
            }
          } else if (sortKey === 'dateCreated') {
            const aWithRaw = a as any;
            const bWithRaw = b as any;
            if (aWithRaw?.dateCreatedRaw != null && bWithRaw?.dateCreatedRaw != null) {
              aDateValue = aWithRaw.dateCreatedRaw;
              bDateValue = bWithRaw.dateCreatedRaw;
            }
          }

          const aDate = parseDate(String(aDateValue));
          const bDate = parseDate(String(bDateValue));

          const aIsValidDate = !isNaN(aDate.getTime());
          const bIsValidDate = !isNaN(bDate.getTime());

          if (aIsValidDate && bIsValidDate) {
            return direction === 'asc'
              ? aDate.getTime() - bDate.getTime()
              : bDate.getTime() - aDate.getTime();
          }

          if (aIsValidDate && !bIsValidDate) return direction === 'asc' ? -1 : 1;
          if (!aIsValidDate && bIsValidDate) return direction === 'asc' ? 1 : -1;

          return direction === 'asc'
            ? String(aDateValue).localeCompare(String(bDateValue))
            : String(bDateValue).localeCompare(String(aDateValue));
        }

        if (!sortType) {
          const aDate = new Date(aText);
          const bDate = new Date(bText);

          const aIsDate = !isNaN(aDate.getTime());
          const bIsDate = !isNaN(bDate.getTime());

          if (aIsDate && bIsDate) {
            return direction === 'asc'
              ? aDate.getTime() - bDate.getTime()
              : bDate.getTime() - aDate.getTime();
          }
        }

        if (sortType === 'number' || (typeof aValue === 'number' && typeof bValue === 'number')) {
          const aNum = Number(aText);
          const bNum = Number(bText);
          return direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        return direction === 'asc' ? aText.localeCompare(bText) : bText.localeCompare(aText);
      });

      setRowsData(sorted);
    } else {
      setRowsData(rows);
    }
  }, [rows, sortConfig, internalSort, columns]);

  function hasAttachments(row: any): row is { attachments: any[] } {
    return row && Array.isArray(row.attachments);
  }

  const renderCellContent = (column: Column<T>, row: T, rowIndex: number): React.ReactNode => {
    const value = column.id === 'actions' ? null : row[column.id as keyof T];

    if (column.render) {
      return column.render(row);
    }

    if (column.id === 'actions') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {!showActionsMenu && (
            <>
              {column.actions?.view && onView && (
                <Tooltip title="View">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(row);
                    }}
                    size="small"
                  >
                    <img src="/images/goicon.svg" height="16px" />
                  </IconButton>
                </Tooltip>
              )}
              {column.actions?.favorite && onFavorite && (
                <Tooltip
                  title={row['favorite' as keyof T] ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <IconButton onClick={() => onFavorite(row)} size="small">
                    {row['favorite' as keyof T] ? (
                      <StarIcon sx={{ color: '#70b595' }} />
                    ) : (
                      <StarBorderIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {column.actions?.attachment && onAttachmentClick ? (
                hasAttachments(rowsData[rowIndex]) && rowsData[rowIndex].attachments.length > 0 ? (
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      color: '#2653F8',
                      verticalAlign: 'middle',
                      width: '90px',
                      padding: 0,
                      alignItems: 'center',
                      display: 'flex',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAttachmentClick?.(row);
                    }}
                  >
                    {`${rowsData[rowIndex].attachments.length} file${rowsData[rowIndex].attachments.length > 1 ? 's' : ''} added`}
                  </Typography>
                ) : (
                  <Box sx={{ width: '90px', padding: 0 }}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onAttachmentClick?.(row);
                      }}
                      size="small"
                    >
                      <AttachFileIcon fontSize="medium" sx={{ color: '#1a55f9' }} />
                    </IconButton>
                  </Box>
                )
              ) : null}

              {column.actions?.delete && onDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(row);
                    }}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <img src="/images/DeleteRed.svg" height="16px" />
                  </IconButton>
                </Tooltip>
              )}
              {column.actions?.edit && onEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(row, rowIndex);
                    }}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          {showActionsMenu && (onEdit || onDuplicate || onDelete || onRename) && (
            <>
              <Tooltip title="More actions">
                <IconButton
                  onClick={(e) => handleActionMenuOpen(e, rowIndex)}
                  size="small"
                  sx={{ px: 1, py: 2 }}
                >
                  <img src="images/action_dots.svg" alt="data-table-actions" />
                </IconButton>
              </Tooltip>
              <ActionMenu
                row={row}
                rowIndex={rowIndex}
                anchorEl={actionMenuState.rowIndex === rowIndex ? actionMenuState.anchorEl : null}
                open={actionMenuState.rowIndex === rowIndex && Boolean(actionMenuState.anchorEl)}
                onClose={handleActionMenuClose}
                onRename={handleRename}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                showEdit={Boolean(onEdit)}
                showDuplicate={Boolean(onDuplicate)}
                showDelete={Boolean(onDelete)}
              />
            </>
          )}
        </Box>
      );
    }

    if (column.tagView && value) {
      return (
        <TagView
          value={value as string}
          onClick={column.clickable ? () => onView?.(row) : undefined}
          isStatus={[
            'status',
            'revenueGrowth',
            'customerExperience',
            'processProductivity',
            'employeeProductivity',
            'costSaving',
          ].includes(column.id?.toString())}
          customColorMap={customColorMap}
        />
      );
    }

    if (column.options?.length) {
      return (
        <DropDownCell
          value={value as string}
          options={column?.options}
          editable={column?.editable}
          align={column?.align}
          onChange={(changedValue: string) =>
            onchange?.(column.label, changedValue, row, rowIndex, column.id)
          }
          placeholder={column?.placeHolder}
        />
      );
    }

    const shouldBeEditable =
      column.editable || (renameColumnId === column.id && editingCell?.rowIndex === rowIndex);

    if (shouldBeEditable) {
      const isCurrentlyEditing =
        editingCell?.rowIndex === rowIndex && editingCell?.columnId === column.id;
      return (
        <EditableTextCell
          value={value as string}
          editable={shouldBeEditable}
          onChange={(changedValue: string) => {
            onchange?.(column.label, changedValue, row, rowIndex, column.id);
            setEditingCell(null);
          }}
          onSaveEdit={onSaveEdit}
          onRename={onRename}
          placeholder={column?.placeHolder}
          isInEditMode={isCurrentlyEditing}
          onEditModeChange={(editing) => {
            if (editing) {
              setEditingCell({ rowIndex, columnId: column.id });
            } else {
              setEditingCell(null);
            }
          }}
          row={row}
          rowIndex={rowIndex}
          columnId={column.id}
        />
      );
    }

    if (React.isValidElement(value)) {
      return (
        <Box
          sx={{
            cursor: column.clickable ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => column.clickable && onView?.(row)}
        >
          {value}
        </Box>
      );
    }

    if (
      typeof value === 'string' &&
      (value?.includes('Ready_#') || value?.includes('Review_#') || value?.includes('Pending_'))
    ) {
      let color = '#000';
      let underlineColor = value.split('_')?.[1];

      return (
        <Box
          sx={{
            color,
            borderRadius: '6px',
            paddingX: '8px',
            paddingY: '6px',
            minHeight: '36px',
            width: '100%',
            cursor: 'pointer',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent:
              column.align === 'left'
                ? 'flex-start'
                : column.align === 'right'
                  ? 'flex-end'
                  : 'center',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '9px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '70%',
              height: '3px',
              backgroundColor: underlineColor,
              borderRadius: '2px',
            },
          }}
          onClick={() => onView?.(row)}
        >
          <Typography>{value.split('_')?.[0]}</Typography>
        </Box>
      );
    }

    const isLinkColumn = column.clickable && (column as any).link === true;

    return (
      <Typography
        variant="body2"
        sx={{
          cursor: column.clickable || isLinkColumn ? 'pointer' : 'default',
          color: isLinkColumn ? '#1A55F9' : 'inherit',
          fontWeight: isLinkColumn ? 500 : 'inherit',
        }}
        onClick={() => column.clickable && onView?.(row)}
      >
        {value as React.ReactNode}
      </Typography>
    );
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}

      {loading ? (
        <Loader message={loadingMessage} height={height} />
      ) : (
        <TableContainer
          sx={{
            maxHeight: height === 'auto' ? '500px' : height,
            overflowY: 'auto',
            minHeight: '200px',
            ...(hideScrollBar && {
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }),
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={String(column.id)}
                    align={column.align || 'left'}
                    sx={{
                      borderBottom: 'none',
                      cursor: column.sortable ? 'pointer' : 'default',
                      verticalAlign: 'middle',
                      '& .MuiTypography-root': {
                        fontWeight: `${column.headerFontWeight ?? headerFontWeight} !important`,
                      },
                    }}
                    style={{
                      minWidth: column.minWidth,
                      width: column?.width,
                      fontWeight: `${headerFontWeight} !important`,
                      backgroundColor: `${column?.bgColor ? column.bgColor : '#F9FAFB'}`,
                      borderBottom: showRowBorders ? '1px solid #DDE1E6' : 'none',
                    }}
                    onClick={() => handleSort(column)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: column.align === 'center' ? 'center' : 'flex-start',
                        color: column?.color || '#313131',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: `${column.headerFontWeight ?? headerFontWeight} !important`,
                          fontStyle: 'normal !important',
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: column?.color || '#313131',
                          marginRight: column.sortable ? '6px' : '0',
                        }}
                      >
                        {column.label}
                      </Typography>
                      {column.sortable && (
                        <img
                          src={
                            (sortConfig?.key || internalSort.key) === column.id
                              ? (sortConfig?.direction || internalSort.direction) === 'asc'
                                ? '/images/sort-vertical.svg'
                                : '/images/sort-vertical.svg'
                              : '/images/sort-vertical.svg'
                          }
                          alt="sort"
                          style={{
                            width: 16,
                            height: 16,
                            flexShrink: 0,
                            opacity: (sortConfig?.key || internalSort.key) === column.id ? 1 : 0.6,
                            verticalAlign: 'middle',
                            marginTop: '-3px',
                          }}
                        />
                      )}
                      {column.tooltip && (
                        <CustomTooltip
                          title={column.tooltip}
                          variant="bottom"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                whiteSpace: 'pre-line',
                                bgcolor: 'white',
                                color: '#666',
                                fontSize: '0.875rem',
                                borderRadius: 2,
                                border: '1px solid #e0e0e0',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                px: 2,
                                py: 1.5,
                                maxWidth: 800,
                                ml: 2,
                                lineHeight: 1.4,
                                '& .MuiTooltip-arrow': {
                                  color: 'white',
                                  '&::before': {
                                    border: '1px solid #e0e0e0',
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <img
                            src="/images/helpBlack.png"
                            alt="help"
                            width={16}
                            height={16}
                            style={{
                              fontSize: 16,
                              cursor: 'pointer',
                              flexShrink: 0,
                              marginLeft: '6px',
                            }}
                          />
                        </CustomTooltip>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsData?.length > 0 &&
                rowsData?.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    tabIndex={-1}
                    onClick={(_e) => {
                      if (!editingCell) {
                        onClick?.(row);
                      }
                    }}
                    sx={{
                      cursor: showRowCursor ? 'pointer' : 'default',
                      ':hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={String(column.id)}
                        align={column.align || 'left'}
                        sx={{
                          borderBottom: showRowBorders ? '1px solid #DDE1E6' : 'none',
                          position: 'relative',
                          verticalAlign: 'middle',
                        }}
                      >
                        {renderCellContent(column, row, rowIndex)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default EnhancedDataTable;
